import { Component, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import { SecurityScope } from '../../auth/auth.model';
import { OpportunityView } from '../../coaching-base/opportunity';
import {
    CoachingSwitchboardService,
    SwitchboardPillarOneComm,
} from '../../coaching-base/switchboard';
import {
    TakeUntilDestroy,
    takeUntilDestroy,
} from '../../common/take-until-destroy.decorator';
import { StateToProps } from '../../store';
import { useDispatcher, UseStore } from '../../store.decorator';
import { SearchUserDialog } from '../../user/search-user.dialog';
import { PeriodType } from '../../widget/date-range.widget.model';
import { navigateToNewMetricId, navigateToNewUser } from '../navigate';
import { watchForNumberParamChange, watchForUserChange } from '../watch-route';

import { CommitmentView } from './commitment-view.model';
import {
    reload,
    reloadCancel,
    reloadUser,
    reloadUserCancel,
    selectMetric,
} from './pillar-one.action';
import { TranslationKeyMap } from './pillar-one.i18n';
import { coachingDetailPillarOneReducer } from './pillar-one.reducer';
import { PillarOneState } from './pillar-one.state';
import { UserView } from './user-view.model';

@Component({
    selector: 'cmps-coaching-detail-pillar-one',
    templateUrl: './pillar-one.component.html',
    host: {
        class: 'cmps-coaching-detail-pillar-one',
    },
})
@TakeUntilDestroy()
@UseStore<PillarOneState>(
    'coachingDetail.pillarOne',
    coachingDetailPillarOneReducer,
)
export class CoachingDetailPillarOneComponent
    implements
        OnInit,
        OnDestroy,
        TakeUntilDestroy,
        StateToProps<PillarOneState>,
        SwitchboardPillarOneComm
{
    state: PillarOneState;

    /** @private */ _i18n = TranslationKeyMap;
    private readonly _readySubj: BehaviorSubject<boolean>;
    private readonly _opportunitySubj: BehaviorSubject<OpportunityView>;
    private readonly _userSubj: BehaviorSubject<UserView>;

    /** @private */ _didFirstLoad: boolean;

    constructor(
        private readonly _router: Router,
        private readonly _activatedRoute: ActivatedRoute,
        private readonly _domSanitizer: DomSanitizer,
        private readonly _userSearchDialog: SearchUserDialog,
        private readonly _switchboardService: CoachingSwitchboardService,
    ) {
        // TODO revise if this or have subscribers be responsible for last emitted
        this._readySubj = new BehaviorSubject(false);
        this._opportunitySubj = new BehaviorSubject(null);
        this._userSubj = new BehaviorSubject(null);
    }

    ngOnInit(): void {
        watchForNumberParamChange(
            this._activatedRoute,
            'metricId',
            this.state.metricId,
        )
            .pipe(
                tap((metricId) => {
                    useDispatcher(
                        this,
                        selectMetric({
                            metricId,
                            nonKpiId:
                                this.state.nonKpiId != null
                                    ? this.state.nonKpiId
                                    : this._activatedRoute.snapshot.queryParams
                                          .nonKpiId,
                        }),
                    );
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        watchForNumberParamChange(
            this._activatedRoute,
            'nonKpiId',
            this.state.nonKpiId,
        )
            .pipe(
                tap((nonKpiId) => {
                    useDispatcher(
                        this,
                        selectMetric({
                            metricId:
                                this.state.metricId != null
                                    ? this.state.metricId
                                    : this._activatedRoute.snapshot.queryParams
                                          .metricId,
                            nonKpiId,
                        }),
                    );
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        watchForUserChange(this._activatedRoute)
            .pipe(
                tap((nextUserId) => {
                    useDispatcher(
                        this,
                        reload({
                            userId: nextUserId,
                            periodTypeId: PeriodType.ROLLING_30,
                            isNonKpi:
                                this._activatedRoute.snapshot.queryParams
                                    .isNonKpi === 'true' ||
                                this._activatedRoute.snapshot.queryParams
                                    .isNonKpi === '1',
                        }),
                    );
                    useDispatcher(this, reloadUser({ userId: nextUserId }));
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        this._switchboardService.pillarOne.connect(this);
    }

    ngOnDestroy(): void {
        useDispatcher(this, reloadCancel({}));
        useDispatcher(this, reloadUserCancel({}));
        this._switchboardService.pillarOne.disconnect();
        this._readySubj.complete();
        this._opportunitySubj.complete();
        this._userSubj.complete();
    }

    stateToProps(prevState?: PillarOneState): void {
        if (prevState.pending.reload !== this.state.pending.reload) {
            this._readySubj.next(!this.state.pending.reload);
        }

        if (prevState.opportunity !== this.state.opportunity) {
            this._opportunitySubj.next(this.state.opportunity);

            // eslint-disable-next-line no-unneeded-ternary
            this._didFirstLoad = !this._didFirstLoad ? true : false;
        }

        if (prevState.user !== this.state.user) {
            this._userSubj.next(this.state.user);
        }
    }

    propsToDispatch(): void {
        this._opportunitySubj.next(this.state.opportunity);
        this._userSubj.next(this.state.user);
    }

    sbReady(): Observable<boolean> {
        return this._readySubj.asObservable();
    }

    sbSelectedOpportunity(): Observable<OpportunityView> {
        return this._opportunitySubj.asObservable();
    }

    sbSelectedUser(): Observable<UserView> {
        return this._userSubj.asObservable();
    }

    /** @private */
    _onUsernameClick(): void {
        this._userSearchDialog.openUsingNgbModal({
            userId: this.state.user.id,
            scope: SecurityScope.COACHING,
            theme: 'next',
            close: (user) => {
                if (!user) {
                    return;
                }

                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                navigateToNewUser(this._router, user.id);
            },
        });
    }

    /** @private */
    _onOppItemClick(opportunity: OpportunityView): void {
        if (opportunity.metricId !== this.state.metricId) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            navigateToNewMetricId(
                this._router,
                this._activatedRoute,
                opportunity.metricId,
                undefined,
                this._didFirstLoad,
            );
        }
    }

    /** @private */
    _onFocusLabelClick(item: CommitmentView): void {
        if (item.commitmentId !== this.state.nonKpiId) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            navigateToNewMetricId(
                this._router,
                this._activatedRoute,
                undefined,
                item.commitmentId,
                this._didFirstLoad,
            );
        }
    }
}
